import React from 'react';
import { ColumnSort, flexRender, Table } from '@tanstack/react-table';
import { getClassNameByHeaderId } from '../util';
import { GTInternalIds } from '../types';
import { GTSort } from './GTSort';
import { GTResizerIcon } from './GTResizerIcon';
import * as S from '../GenericTable.styles';

interface GTHeadProps<T> {
  table: Table<T>;
  controledSorting?: { state: ColumnSort; onChange: (a: ColumnSort) => void };
  enableColumnResize: boolean;
  itemHeight: number;
}

export const GTHead = <T,>({ table, controledSorting, enableColumnResize, itemHeight }: GTHeadProps<T>) => {
  return (
    <thead>
      {table.getHeaderGroups().map((headerGroup) => {
        return (
          <tr key={headerGroup.id} data-cy="gt-tr">
            <th className="gt-left-spacer" />
            {headerGroup.headers.map((header) => {
              const { id, column, isPlaceholder } = header;
              const canSort = column.getCanSort();
              const isSorted = column.getIsSorted();
              const isAsc = isSorted === 'asc';
              const isInternalCol =
                id === GTInternalIds.checker || id === GTInternalIds.expander || id === GTInternalIds.tdm;

              const sortHandler = controledSorting
                ? () => {
                    controledSorting.onChange({
                      id: id,
                      desc: !controledSorting.state.desc,
                    });
                  }
                : column.getToggleSortingHandler();

              let headerTitle = '';
              if (typeof column.columnDef.header === 'string') {
                headerTitle = column.columnDef.header;
              }

              return (
                <S.TH
                  key={id}
                  style={{
                    width: header.getSize(),
                    minWidth: isInternalCol ? 40 : undefined,
                    maxWidth: column.columnDef.maxSize,
                    tableLayout: isInternalCol ? 'fixed' : undefined,
                  }}
                  className={getClassNameByHeaderId(id)}
                  $itemHeight={itemHeight}
                >
                  <S.HeaderContent
                    $cursorPointer={canSort}
                    onClick={canSort ? sortHandler : undefined}
                    title={headerTitle}
                    $canOverflow={isInternalCol}
                  >
                    <div className="header-text">
                      {!isPlaceholder && flexRender(column.columnDef.header, header.getContext())}
                    </div>
                    <div className="header-sort">
                      {controledSorting && controledSorting.state.id === id && (
                        <GTSort isSorted isAsc={!controledSorting.state.desc} />
                      )}
                      {!controledSorting && canSort && <GTSort isSorted={!!isSorted} isAsc={isAsc} />}
                    </div>
                  </S.HeaderContent>
                  {enableColumnResize && !isInternalCol && (
                    <div
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
                    >
                      <div className="resizer-icon">
                        <GTResizerIcon />
                      </div>
                    </div>
                  )}
                </S.TH>
              );
            })}
          </tr>
        );
      })}
    </thead>
  );
};
